import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTray } from "@/lib/clipboard-tray";
import { useLibrary } from "@/lib/library";
import { resolvePackItems, encodePack, type PackItem } from "@/lib/pack";
import { copyText } from "@/lib/copy";

interface Candidate extends PackItem {
  source: string;
}

/**
 * Curate a pack from the tray, favourites and recently-viewed, give it an
 * optional title, and produce a stateless share link (the whole pack lives in
 * the URL — see src/lib/pack.ts). No backend, no account.
 */
export function PackBuilder() {
  const tray = useTray();
  const { favorites, recentlyViewed } = useLibrary();

  const items = useMemo(() => {
    const out: Candidate[] = [];
    const seen = new Set<string>();
    const add = (symbols: string[], source: string) => {
      for (const it of resolvePackItems(symbols)) {
        if (seen.has(it.symbol)) continue;
        seen.add(it.symbol);
        out.push({ ...it, source });
      }
    };
    add(tray.items, "Tray");
    add(favorites, "Favorites");
    add(
      recentlyViewed.map((e) => e.symbol),
      "Recent",
    );
    return out;
  }, [tray.items, favorites, recentlyViewed]);

  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [title, setTitle] = useState("");
  const [copied, setCopied] = useState(false);

  function toggle(symbol: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  }

  const chosen = items.filter((i) => selected.has(i.symbol));
  const shareUrl = useMemo(() => {
    if (chosen.length === 0) return "";
    const token = encodePack({ title: title.trim(), symbols: chosen.map((c) => c.symbol) });
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/pack/${token}`;
  }, [chosen, title]);

  async function copyLink() {
    if (!shareUrl) return;
    const ok = await copyText(shareUrl, { source: "pack-link" });
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  if (items.length === 0) {
    return (
      <p className="mt-8 text-muted-foreground">
        Nothing to curate yet. Add symbols to your clipboard tray or favourite a few, then come back
        to bundle them into a shareable pack.
      </p>
    );
  }

  return (
    <div className="mt-8">
      <label className="block text-sm font-medium text-foreground/90" htmlFor="pack-title">
        Pack title <span className="text-muted-foreground">(optional)</span>
      </label>
      <input
        id="pack-title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={120}
        placeholder="e.g. My favourite arrows"
        className="mt-2 w-full rounded-lg border border-border bg-surface/60 px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus-visible:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />

      <div className="mt-5 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Choose symbols</h2>
        <span className="text-sm text-muted-foreground">{chosen.length} selected</span>
      </div>

      <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {items.map((item) => {
          const on = selected.has(item.symbol);
          return (
            <li key={item.symbol}>
              <button
                type="button"
                onClick={() => toggle(item.symbol)}
                aria-pressed={on}
                className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                  on
                    ? "border-primary/50 bg-primary/10"
                    : "border-border bg-surface/40 hover:border-primary/30"
                }`}
              >
                <span className="text-2xl leading-none">{item.symbol}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-foreground">{item.name}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {item.source}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={`text-sm ${on ? "text-primary" : "text-muted-foreground"}`}
                >
                  {on ? "✓" : "+"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {chosen.length > 0 ? (
        <div className="mt-6 rounded-xl border border-border bg-surface/40 p-4">
          <p className="text-sm font-medium text-foreground/90">Your share link</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-surface/60 px-3 py-2 font-mono text-xs text-foreground">
              {shareUrl}
            </code>
            <button
              type="button"
              onClick={copyLink}
              className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
            >
              {copied ? "Copied!" : "Copy link"}
            </button>
            <Link
              to="/pack/$data"
              params={{ data: shareUrl.split("/pack/")[1] ?? "" }}
              className="rounded-md border border-border bg-surface/60 px-4 py-2 text-sm font-medium text-foreground hover:border-primary/40"
            >
              Preview
            </Link>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Anyone with this link sees the same pack — it's stored entirely in the URL, with no
            account or server.
          </p>
        </div>
      ) : null}
    </div>
  );
}
